
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface OverdueTask {
  id: string;
  title: string;
  due_date: string;
  board_id: string;
  board_name: string;
  client_name: string;
  column_name: string;
}

interface OverdueTasksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tasks: OverdueTask[];
  isLoading: boolean;
}

export function OverdueTasksDialog({ open, onOpenChange, tasks, isLoading }: OverdueTasksDialogProps) {
  const navigate = useNavigate();

  const handleTaskClick = (boardId: string) => {
    navigate(`/workflow?board=${boardId}`);
    onOpenChange(false);
  };

  const getDaysOverdue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-red-500" />
            Tarefas em Atraso ({tasks.length})
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-medium">Parabéns! Nenhuma tarefa em atraso</p>
              <p className="text-sm">Todas as suas tarefas estão em dia</p>
            </div>
          ) : (
            tasks.map((task) => (
              <div
                key={task.id}
                className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleTaskClick(task.board_id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{task.title}</h3>
                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                      <span>{task.client_name}</span>
                      <span>•</span>
                      <span>{task.board_name}</span>
                      <span>•</span>
                      <span>{task.column_name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="destructive" className="text-xs">
                        {getDaysOverdue(task.due_date)} dia(s) em atraso
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Vencimento: {format(new Date(task.due_date), "dd/MM/yyyy", { locale: ptBR })}
                      </span>
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
              </div>
            ))
          )}
        </div>
        
        {tasks.length > 0 && (
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
